/**
 * Typed client for the QMS API.
 *
 * Same origin in every environment — the IngressRoute serves the API under /api on
 * the same host as this app, and Vite proxies /api in development. That is
 * deliberate: no CORS configuration to get wrong, and no cross-site cookie
 * behaviour to work around.
 */
import { accessToken, useTokens } from './auth'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** A 401 means sign in; a 403 means you are signed in and still not allowed. */
  get isAuth() {
    return this.status === 401
  }
  get isForbidden() {
    return this.status === 403
  }
  get isConflict() {
    return this.status === 409
  }
}

const base = window.__QMS_CONFIG__?.apiBase ?? ''

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body) headers.set('Content-Type', 'application/json')

  if (useTokens) {
    const token = await accessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${base}${path}`, { ...init, headers })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let body: unknown
  let isJson = true
  try {
    body = text ? JSON.parse(text) : undefined
  } catch {
    // Kept for the error path below, where a non-JSON body is still worth reporting.
    body = text
    isJson = false
  }

  if (!res.ok) {
    const b = body as { error?: string; hint?: string; code?: string } | undefined
    // The API names configuration-class auth failures. Prefer the hint: "add upn as
    // an optional claim" is actionable where "not authenticated" is not.
    const msg = b?.hint ?? b?.error ?? `${res.status} ${res.statusText}`
    throw new ApiError(res.status, msg, body)
  }

  // A success status carrying something that is not JSON is a broken response, not data.
  // Returning the raw text as T let a *string* stand in for the object the caller asked
  // for: every property read off it came back undefined, far from where the response was
  // handled. That is how a 200 carrying index.html became a blank page instead of an
  // error (#1) — run this image with no qms-api behind it and nginx's history fallback
  // answers /api/v1/me exactly that way.
  if (!isJson) {
    const type = res.headers.get('content-type') ?? 'no content-type'
    throw new ApiError(res.status, `${path} answered ${res.status} with ${type}, not JSON`, body)
  }
  return body as T
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// --- types -----------------------------------------------------------------

export type Projection = 'public' | 'operational' | 'assurance' | 'audit'
export type DrillDown = 'full' | 'derived' | 'limited'

export interface PartyRef {
  /**
   * 'email' names somebody without identifying them: an address the register knows, with no
   * directory object behind it. It exists because an access approver may have no reason to be a
   * user of this system, and the directory mirror only fills in as the Entra connector runs.
   */
  type: 'person' | 'position' | 'email'
  id: string
  display?: string
}

/**
 * A response array, guaranteed to be an array.
 *
 * The API now serialises an empty collection as [], but a client that crashes when it does not
 * is a client one server change away from a blank page — which is exactly what happened: an
 * asset with no linked risks received null, the page read .length on it, and rendered nothing
 * but the sidebar. Defending here costs a function call and removes the whole failure mode.
 */
export function arr<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : []
}

export type AiToolType = 'assistant' | 'mcp_server' | 'cli' | 'embedded_saas_feature'

export type AiApprovalStatus =
  | 'not_assessed' | 'approved' | 'approved_with_conditions' | 'rejected' | 'withdrawn'

/** An approval as the published list carries it. Dates are RFC3339. */
export interface AiToolApproval {
  status: AiApprovalStatus
  /** Empty or absent means all of ABAX Group. */
  scopeOrgUnitIds?: string[] | null
  conditions?: string
  approvedBy?: string
  approvedAt?: string
  expiresAt?: string
  withdrawnAt?: string
  withdrawnReason?: string
  caseRef?: string
}

export interface AiTool {
  assetId: string
  title: string
  aiToolType?: AiToolType
  /** Family codes. */
  aiModelFamilies?: string[] | null
  /** Families this tool routes to that no longer allow use (approval ended, or a status that forbids it). Flagged, never hidden. */
  familyIssues?: string[] | null
  /** Null when the tool names no supplier the register knows. */
  supplier: { id: string; title: string; aiToolApproved?: AiApprovalStatus } | null
  approval: AiToolApproval
  /** Computed by the API from status and expiry. Never recompute it. */
  active: boolean
  /**
   * Who holds a per-user tool (an assistant): group, org units, and a count of named people.
   * Null for a tool approved as a whole, whose approval scope is the whole answer. Never names a
   * person, for any reader.
   */
  entitlements?: AiToolEntitlements | null
}

export interface AiToolEntitlements {
  group: boolean
  orgUnitIds: string[] | null
  people: number
}

export type EntitlementScopeType = 'group' | 'orgUnit' | 'person'

/** What ended, or will end, an entitlement. Computed by the API on every read. */
export type EntitlementEndReason =
  | 'entitlement_expired' | 'entitlement_revoked' | 'tool_approval_withdrawn' | 'tool_approval_not_granting'
  | 'tool_approval_expired' | 'tool_retired' | 'tool_not_per_user' | 'scope_outside_approval'
  | 'person_left' | 'person_post_ended'

export interface EntitlementStatus {
  active: boolean
  /** Null when nothing is coming, or when something ended it at a time the register does not know. */
  endsAt: string | null
  endedBy?: EntitlementEndReason
}

/** A tool the caller may use right now, and why. */
export interface MineTool extends AiTool {
  via: 'approval' | 'entitlement'
  scopeType?: EntitlementScopeType
  endsAt: string | null
  endedBy?: EntitlementEndReason
  endedByLabel?: string
}

export interface AiToolsMine {
  tools: MineTool[] | null
  /** The org units of the positions the caller currently holds. */
  orgUnits: { code: string; name: string }[] | null
}

/** An entitlement as stored. What a revoke returns, and the entries of a grant's response. */
export interface EntitlementRecord {
  id: string
  toolId: string
  scope: { type: EntitlementScopeType; orgUnitIds?: string[] | null; personId?: string }
  reason: string
  caseRef?: string
  grantedBy: string
  grantedByName?: string
  grantedAt: string
  expiresAt: string
  revokedAt?: string
  revokedBy?: string
  revokedReason?: string
}

/** An entitlement in the list: the record plus the tool's name and the computed status. */
export interface EntitlementRow extends EntitlementRecord {
  toolTitle: string
  toolType?: AiToolType
  personName?: string
  personUpn?: string
  status: EntitlementStatus
  endedByLabel?: string
}

export interface EntitlementList {
  entitlements: EntitlementRow[] | null
  /** Records that could not be read and are missing from the list. Above zero is an incident. */
  unreadable?: number
}

export interface GrantResult {
  entitlements: EntitlementRecord[] | null
  /** True when the scope includes the approver who granted it. */
  coversApprover: boolean
  notice?: string
}

export interface GrantEntitlementBody {
  toolId: string
  scope: { type: EntitlementScopeType; orgUnitIds?: string[]; personIds?: string[] }
  reason: string
  caseRef?: string
  /** The last day it is valid, YYYY-MM-DD. */
  expiresAt: string
}

export interface PersonChoice {
  id: string
  name: string
  upn: string
}

export interface AiToolList {
  tools: AiTool[] | null
  families: { code: string; name: string; status: string; approvalExpires: string | null; expired: boolean }[] | null
  orgUnits: { code: string; name: string }[] | null
}

export interface Asset {
  id: string
  category: string
  categoryName: string
  drillDown: DrillDown
  title: string
  status: string
  ownerRef: PartyRef
  orgUnitId?: string
  projection: Projection
  // operational and above
  description?: string
  technicalOwnerRef?: PartyRef
  /** Who approves access — may be a directory person who never logs in here, or a role. */
  accessApproverRef?: PartyRef
  /** Operational notes for whoever administers the system. Withheld from the employee view. */
  itNotes?: string
  legalEntityIds?: string[]
  locationIds?: string[]
  standardsInScope?: string[]
  systemProfile?: SystemProfile | null
  systemsOfRecord?: SystemOfRecord[] | null
  instances?: InstanceRollup
  parentAssetId?: string
  dependsOnAssetIds?: string[]
  supplierId?: string
  source?: string
  externalRef?: string
  tags?: string[]
  version?: number
  updatedAt?: string
  // assurance and above
  classification?: string
  cia?: { c: number; i: number; a: number }
  criticality?: string
  bia?: BIA | null
  personalData?: PersonalData | null
  review?: Review
  riskIds?: string[]
  /** Set by the server once the asset has a classification record. */
  classificationRecordId?: string
  /** The asset's own answers: the AI facet read from the live approved record, where it has one. */
  aiRiskTier?: ClassTier | null
  /** The version an Art. 5 finding was recorded on, when the tier is prohibited because of one. */
  aiRiskTierFindingVersion?: number | null
  /** provider, deployer or provider_and_deployer; empty when blanked or undetermined. */
  aiActRole?: string
  autonomyLevel?: string
  reAuditDue?: string | null
  /** Present only for callers who may read the record. */
  aiClassification?: AssetClassification
  embedsAi?: boolean
}

/**
 * Where an asset is actually administered, when that is somewhere else.
 *
 * The case that produced it: employees are a register asset, but the HR system
 * holding them is not something to mirror into a compliance tool. "Maintained in
 * HiBob, here is the link, here is why we do not sync it" is a real answer; an empty
 * drill-down is not.
 */
export interface SystemOfRecord {
  system: string
  purpose?: string
  url?: string
  assetId?: string
  accessedVia?: string
  integrated?: boolean
  noIntegrationReason?: string
}

export interface BIA {
  rtoMinutes?: number
  rpoMinutes?: number
  mtpdHours?: number
  businessCritical?: boolean
}

export interface PersonalData {
  contains?: boolean
  specialCategory?: boolean
  categories?: string[]
  dataSubjects?: string[]
}

export interface SystemProfile {
  hostingType?: string
  hostingNotes?: string
  vendorUrl?: string
  accessManagedByIt?: string
  sso?: string
  ssoProtocol?: string
  mfaEnforced?: string
  scimProvisioning?: string
  criticalItSystem?: boolean
  dataResidency?: string[]
  licence?: { notes?: string; lastReviewDate?: string; seats?: number; renewalDate?: string } | null
  deprecation?: { deprecated: boolean; notes?: string; plannedEol?: string } | null
}

export interface InstanceRollup {
  total: number
  compliant: number
  nonCompliant: number
  unknown: number
  staleOver30d: number
  unencrypted: number
  publiclyExposed: number
  sources?: string[]
  lastSyncAt?: string | null
  health?: string
}

export interface Review {
  cadence?: string
  lastReviewedAt?: string | null
  nextReviewDue?: string | null
  attestationStatus?: string
}

export interface AssetStats {
  total: number
  byCategory: Record<string, number>
  byCriticality: Record<string, number>
  byClassification: Record<string, number>
  missingOwner: number
  missingTechnicalOwner: number
  missingClassification: number
  overdueReview: number
  neverReviewed: number
  ownerLoad: { owner: PartyRef; count: number; overdue: number }[]
}

export interface Assessment {
  likelihood?: number
  consequence?: number
  score?: number
  band?: string
  rationale?: string
  assessedAt?: string
  assessedBy?: string
  acceptedBy?: string
  acceptedAt?: string
  justification?: string
}

export interface TreatmentPlan {
  description?: string
  ownerRef?: PartyRef
  dueDate?: string
  startedAt?: string
  completedAt?: string
  existingControls?: string[]
  plannedControls?: string[]
  note?: string
}

export interface HistoryEvent {
  at: string
  kind: 'change' | 'assessment'
  action?: string
  actor?: string
  reason?: string
  changes?: string[]
  inherent?: { likelihood: number; consequence: number; score: number; band?: string }
  residual?: { likelihood: number; consequence: number; score: number; band?: string }
  status?: string
  treatment?: string
  rationale?: string
}

export interface RiskHistory {
  riskId: string
  events: HistoryEvent[]
  /** Set when a source could not be read: what is shown is real but not complete. */
  incomplete?: string
  note?: string
}

export interface RiskMatrixLevel {
  value: number
  label: string
  anchor: string
  dimensions?: { name: string; descriptor: string }[]
}

export interface RiskBandInfo {
  band: string
  label: string
  action: string
  acceptedBy: string
  treatmentRequired?: boolean
}

export interface RiskMatrixCell {
  likelihood: number
  consequence: number
  score: number
  band: string
}

export interface RiskMatrix {
  scale: number
  scoreMax: number
  ceiling: number
  likelihood: RiskMatrixLevel[]
  consequence: RiskMatrixLevel[]
  bands: RiskBandInfo[]
  cells: RiskMatrixCell[]
  notes: string[]
}

export interface Role {
  id: string
  title: string
  description?: string
  orgUnitId?: string
  legalEntityIds?: string[]
  locationIds?: string[]
  confersPermissions?: string[]
  holders?: RoleHolder[]
  vacant: boolean
  status: string
  archivedReason?: string
  archivedAt?: string | null
}

export interface RoleHolder {
  personId: string
  upn?: string
  name?: string
  from: string
  until?: string | null
  note?: string
  interim?: boolean
  endedBy?: string
  endedReason?: string
}

export interface RoleRow {
  position: Role
  /** "CISO (Thomas Ornell)" — resolved server-side so every client agrees. */
  display: string
  /** Tenures in force. */
  holders?: RoleHolder[]
  /** Every tenure, current first — the trail of who has held this role. */
  history?: RoleHolder[]
}

export interface Suggestion {
  type: 'threat' | 'opportunity'
  title: string
  scenario?: { threat?: string; vulnerability?: string; cause?: string; consequenceDescription?: string }
  category?: string
  standards?: string[]
  /** The avoid/reduce/share/retain/exploit decision — an enum, not prose. */
  suggestedTreatment?: string
  rationale: string
  basis?: string[]
  /** The proposed treatment plan. None of this is a rating. */
  treatmentPlan?: string
  existingControls?: string[]
  plannedControls?: string[]
  /** Annex A references, already validated against the library server-side. */
  controlIds?: string[]
  ownerRole?: string
  dueWithinDays?: number
  affectedAssetIds?: string[]
}

export interface SuggestionResult {
  assetId: string
  suggestions: Suggestion[]
  model: string
  /** Where inference ran. Shown, because "which model, where" is a fair question. */
  region: string
  promptVersion: string
  generatedAt: string
  note: string
}

export interface CategoryEntry {
  code: string
  name: string
  drillDown: 'full' | 'derived' | 'limited'
  description?: string
  deprecated?: boolean
  builtIn?: boolean
  inUse: number
}

export type OrgRefKind = 'legalEntities' | 'orgUnits' | 'locations'

export interface OrgRefEntry {
  code: string
  name: string
  country?: string
  orgNumber?: string
  parentCode?: string
  entityCode?: string
  address?: string
  description?: string
  deprecated?: boolean
  inUse: number
}

export interface OrgRefList {
  kind: OrgRefKind
  label: string
  entries: OrgRefEntry[]
  /** Codes the asset data uses that the list does not contain. */
  unregistered: string[]
  version?: number
}

export interface CheckOption {
  value: string
  label: string
  tone?: string
}

export interface CheckItem {
  id: string
  text: string
  set: 'status' | 'yesno'
  hint?: string
}

export interface CheckGroup {
  title?: string
  items: CheckItem[]
}

export interface CheckSection {
  id: string
  title: string
  /** The Annex A control this section verifies — the reason the checklist is evidence. */
  controlId?: string
  groups: CheckGroup[]
}

export interface CheckTemplate {
  id: string
  version: number
  title: string
  intro?: string
  cadence?: string
  sections: CheckSection[]
  source?: string
}

export interface CheckAnswer {
  value: string
  comment?: string
  at?: string
  by?: string
}

export interface CheckRun {
  id: string
  templateId: string
  templateVersion: number
  title: string
  locationId: string
  /** The office as a register asset, e.g. PHY-2, which is what somebody picked. */
  locationAssetId?: string
  locationName?: string
  performedOn: string
  performedById: string
  performedByName?: string
  participants?: string[]
  answers: Record<string, CheckAnswer>
  status: 'draft' | 'submitted'
  submittedAt?: string
  submittedBy?: string
  note?: string
  riskIds?: string[]
  version?: number
}

export interface CheckSummary {
  items: number
  answered: number
  ok: number
  deviations: number
  toBeAssessed: number
  notChecked: number
  yes: number
  no: number
  deviationsWithoutComment?: string[]
  controlsVerified?: Record<string, { items: number; ok: number; deviations: number; unanswered: number }>
  complete: boolean
}

export interface CheckRunDetail {
  run: CheckRun
  template: CheckTemplate
  summary: CheckSummary
  answerSets: Record<string, CheckOption[]>
  canEdit: boolean
}

export interface ChecklistInfo {
  id: string
  version: number
  title: string
  intro?: string
  cadence?: string
  items: number
  sections: number
  controls?: string[]
  lastCompletedByLocation?: Record<string, { id: string; performedOn: string; by?: string }>
}

export interface Allowance {
  id: string
  scope: 'instance' | 'asset' | 'pattern'
  source: string
  findingKey: string
  assetId?: string
  instanceId?: string
  pattern?: string
  justification: string
  compensatingControls?: string[]
  controlIds?: string[]
  riskId?: string
  status: 'proposed' | 'approved' | 'revoked'
  requestedBy: string
  requestedName?: string
  requestedAt: string
  approvedBy?: string
  approvedName?: string
  approvedAt?: string | null
  expiresAt: string
  revokedReason?: string
}

export interface AllowanceRow {
  allowance: Allowance
  active: boolean
  expired: boolean
  expiringSoon: boolean
}

export interface FindingSample {
  instanceId: string
  assetId: string
  name: string
  accepted: boolean
  allowanceId?: string
}

export interface FindingGroup {
  source: string
  findingKey: string
  detail: string
  count: number
  accepted: number
  open: number
  assetIds: string[]
  examples: FindingSample[]
  allowances?: Allowance[]
}

export interface Discovery {
  id: string
  source: string
  suggestedTitle: string
  suggestedCategory: string
  kind?: string
  example?: string
  state?: string
  promotedAssetId?: string
  dismissedReason?: string
  /** What promoting this would take on. Findings only become instance-level once promoted. */
  count?: number
  nonCompliant?: number
  publiclyExposed?: number
  findingsByKey?: Record<string, number>
}

export interface Risk {
  id: string
  legacyId?: string
  title: string
  type: 'threat' | 'opportunity'
  status: string
  category?: string
  standards?: string[]
  ownerRef: PartyRef
  assetIds?: string[]
  inherent: Assessment
  residual: Assessment
  appetiteBreach: boolean
  treatment?: string
  plan?: TreatmentPlan
  scenario?: { threat?: string; vulnerability?: string; cause?: string; consequenceDescription?: string }
  review?: Review
  version?: number
  source?: string
  createdAt?: string
  updatedAt?: string
}

export interface RiskStats {
  total: number
  byStatus: Record<string, number>
  byType: Record<string, number>
  byBand: Record<string, number>
  needsRescoring: number
  overdueReview: number
  appetiteBreach: number
  opportunities: number
  noAssetLink: number
  noControlLink: number
  ownerLoad: { owner: PartyRef; count: number; overdue: number }[]
  heatmap: number[][]
}

export interface Control {
  id: string
  theme: string
  themeName?: string
  title: string
  measuredFrom?: { signal: string; source: string } | null
  crossStandard?: Record<string, string[]>
}

export interface SoAEntry {
  controlId: string
  theme: string
  title: string
  applicable: boolean | null
  justification?: string
  implementationIds?: string[]
  riskIds?: string[]
  effectiveness?: string
  version?: number
  decidedBy?: string
  decidedAt?: string
  updatedAt?: string
  /** Carried over from the previous compliance tool: what we actually do. */
  implementation?: string
  evidence?: string[]
  processes?: string[]
  ownerRole?: string
  /** Self-assessed completion percentage from the source tool. */
  degree?: number
  status?: string
  source?: string
  sourceUpdated?: string
}

export interface EvidenceRef {
  kind: 'link' | 'asset' | 'risk' | 'control' | 'process' | 'document' | 'measured'
  label: string
  /** Absolute URL for external evidence, in-app path for a register object, empty when neither. */
  target?: string
  external?: boolean
  /** Why a reference cannot be followed — shown instead of leaving a dead label. */
  note?: string
}

export interface MeasuredEvidence {
  signal: string
  source: string
  label?: string
  target?: string
  measurement?: { numerator: number; denominator: number; percent: number; measuredAt: string }
  effectiveness?: string
  available: boolean
  note?: string
}

export interface SoAEntryDetail {
  control: {
    id: string; theme: string; themeName?: string; title: string
    standard?: string; crossStandard?: Record<string, string[]>
  }
  entry: SoAEntry
  /** False when no line has ever been written — different from a line with empty fields. */
  decided: boolean
  linkedRisks: { id: string; title: string; type: string; band?: string }[]
  /** The evidence text, classified into things a reviewer can follow. */
  evidenceRefs: EvidenceRef[]
  /** Documents from the register: proof somebody can open, and proposals to confirm. */
  linkedDocuments: DocumentRef[] | null
  candidateDocuments: DocumentRef[] | null
  /** Computed from live register data where the control library declares a signal. */
  measurement?: MeasuredEvidence | null
  editable: boolean
}

export interface SoAReport {
  /** Applicable controls described in prose but not linked to an implementation record. */
  describedNotLinked?: string[]
  generatedAt: string
  standard: string
  entries: SoAEntry[]
  total: number
  applicable: number
  excluded: number
  undecided: number
  missingJustification: string[]
  noImplementation: string[]
  noRiskReference: string[]
  ineffective: string[]
  auditReady: boolean
}

export interface Me {
  personId: string
  upn: string
  name: string
  enabled: boolean
  roles: string[]
}

export interface Instance {
  id: string
  registerAssetId: string
  source: string
  kind: string
  name: string
  status: string
  assignedToPersonId?: string
  locationId?: string
  signals?: Record<string, unknown>
  attributes?: Record<string, unknown>
  lastSyncedAt?: string
}

// --- access credentials ----------------------------------------------------

export interface Credential {
  id: string
  assetId: string
  locationId: string
  label: string
  kind: string
  state: string
  description?: string
  zones?: string[]
  currentLoanId?: string
  heldByName?: string
  heldByPersonId?: string
  dueBackAt?: string | null
  lostNote?: string
}

export interface Loan {
  id: string
  credentialId: string
  credentialLabel: string
  credentialKind: string
  assetId: string
  locationId: string
  borrower: {
    personId?: string
    name: string
    email?: string
    company?: string
    homeLocationId?: string
    employee: boolean
  }
  purpose?: string
  issuedBy: string
  issuedByName?: string
  issuedAt: string
  expectedReturnAt?: string | null
  returnedAt?: string | null
  returnedToName?: string
  returnNote?: string
  state: 'out' | 'returned' | 'lost'
}

export interface AccessStats {
  credentials: number
  available: number
  onLoan: number
  assigned: number
  lost: number
  overdueLoans: number
  longestOutstandingDays: number
  longestOutstandingId?: string
}

// --- role administration ---------------------------------------------------

export interface PermissionInfo {
  role: string
  name: string
  basis: string
  summary: string
  grants: string[]
  scoped: boolean
  defaultScopeType: string
  privileged: boolean
  requiresExpiry: boolean
  implicit: boolean
  /** Never granted by name; a position confers it. */
  conferredOnly?: boolean
}

export interface ScopeTypeOption {
  type: string
  label: string
}

export interface Person {
  id: string
  upn: string
  name?: string
  enabled: boolean
  department?: string
  jobTitle?: string
  source: string
}

/**
 * One Entra user as a lookup result.
 *
 * `enabled` is nullable on purpose: the directory not saying is not the same as the directory
 * saying no, and showing a working colleague as a disabled account on the strength of a field that
 * never arrived would be a lie with consequences — the register's enabled flag drives leaver
 * revocation.
 */
export interface DirectoryCandidate {
  id: string
  upn: string
  name?: string
  mail?: string
  department?: string
  jobTitle?: string
  enabled: boolean | null
  alreadyHere: boolean
}

export interface Assignment {
  id: string
  personId?: string
  upn: string
  /** The permission granted. Kept as `role` on the wire: the audit trail already
   *  references that field name and history is not rewritten. */
  role: string
  /** The role that conferred it, empty when it was added on top. */
  conferredByRole?: string
  scope?: { type: string; id?: string }
  grantedBy?: string
  grantedAt?: string
  reason?: string
  expiresAt?: string | null
  confirmedBy?: string
  state: string
}

// --- endpoints -------------------------------------------------------------


/* ── GDPR Article 30 record of processing activities ───────────────────────── */

/** Why a transfer outside the EEA is lawful. "unknown" is the honest imported state. */
export type TransferBasis = 'none' | 'adequacy' | 'scc' | 'bcr' | 'derogation' | 'unknown'

export interface Transfer {
  country: string
  recipient?: string
  basis: TransferBasis
  safeguards?: string
  notes?: string
}

/** Where a row came from, so an imported defect stays attributable to the source. */
export interface RoPASource {
  file?: string
  sheet?: string
  row?: number
  verbatim?: Record<string, string>
}

export interface RoPAStore {
  path?: string[] | null
  name: string
  purpose?: string
  rawCategories?: string
  supportingSystem?: string
  assetId?: string
  measures?: string[] | null
  retention?: string
  source?: RoPASource
}

export interface RoPARegister {
  role: 'processor' | 'controller'
  title?: string
  organisationName?: string
  organisationContact?: string
  legalEntityCode?: string
  controllerCategory?: string
  dpoName?: string
  dpoContact?: string
  representativeName?: string
  representativeContact?: string
  owner?: PartyRef
  reviewedOn?: string
  reviewIntervalMonths?: number
  source?: RoPASource
  updatedAt?: string
  updatedBy?: string
}

export interface ProcessingActivity {
  id: string
  department?: string
  article6Basis?: string
  article9Basis?: string
  legitimateInterest?: string
  liaLink?: string
  jointController?: string
  processorContract?: string
  dpaInHoC?: string
  dpiaIncluded?: string
  dpiaProgress?: string
  dpiaLink?: string
  automatedDecisions?: string
  dataSource?: string
  rightsAvailable?: string
  consentRecordLink?: string
  retentionPolicyLink?: string
  retentionAdheres?: string
  breachOccurred?: string
  breachLink?: string
  systemNames?: string[] | null
  role: 'processor' | 'controller'
  name: string
  status: 'draft' | 'active' | 'superseded' | 'retired'
  purpose?: string
  description?: string
  categories?: string[] | null
  rawCategories?: string
  dataSubjects?: string[] | null
  dataCategories?: string[] | null
  specialCategory?: boolean
  lawfulBasis?: string
  recipients?: string[] | null
  transfers?: Transfer[] | null
  measures?: string[] | null
  retention?: string
  assetIds?: string[] | null
  controlIds?: string[] | null
  riskIds?: string[] | null
  stores?: RoPAStore[] | null
  owner?: PartyRef
  source?: RoPASource
  updatedAt?: string
  version: number
}

/**
 * One field's standing against one part of Article 30.
 *
 * `strength` is the article's own wording, not a severity we invented: mandatory fields have no
 * qualifier in the text, and the two "where possible" ones must not be rendered as failures.
 */
export interface Article30Check {
  /**
   * Which question this check answers. `record` is Article 30 completeness; `lawfulness` is
   * Articles 6, 9, 28 and 35 — whether the processing the record describes is lawful at all.
   * Never merged: a record can be perfectly complete and describe unlawful processing.
   */
  kind: 'record' | 'lawfulness'
  article: string
  field: string
  strength: 'mandatory' | 'whereApplicable' | 'wherePossible'
  state: 'ok' | 'weak' | 'missing' | 'notApplicable'
  detail?: string
}

export interface Article30Assessment {
  checks: Article30Check[] | null
  mandatoryTotal: number
  mandatoryOk: number
  weak: number
  missing: number
  concerns: number
}

export interface RoPASummary {
  activities: number
  stores: number
  draft: number
  incomplete: number
  withTransfers: number
  weakTransfers: number
  unresolvedStores: number
  duplicateMeasures: number
  commonMeasures?: string
  concerns: number
  byArticle?: Record<string, number>
}

export interface RoPAList {
  items: { activity: ProcessingActivity; assessment: Article30Assessment }[] | null
  count: number
  role: 'processor' | 'controller'
  register: RoPARegister | null
  summary: RoPASummary
  review: { neverReviewed: boolean; overdue: boolean; monthsOverdue: number }
  canEdit: boolean
  categories: string[] | null
  bases: TransferBasis[] | null
  article6Bases?: string[] | null
  article9Bases?: string[] | null
  departments?: string[] | null
}

export interface RoPADetail {
  activity: ProcessingActivity
  register: RoPARegister | null
  assessment: Article30Assessment
  suggestedCategories: string[] | null
  categories: string[] | null
  bases: TransferBasis[] | null
  article6Bases?: string[] | null
  article9Bases?: string[] | null
  canEdit: boolean
}

export interface RoPAExport {
  role: string
  register: RoPARegister | null
  rows: Record<string, unknown>[] | null
  generatedAt: string
  summary: RoPASummary
}


/* ── Supplier register ──────────────────────────────────────────────────────── */

/**
 * Where a supplier record came from, and therefore who owns which fields.
 *
 * Shown wherever a supplier is, because the two are not interchangeable: a mirrored record's
 * identity fields are overwritten on the next sync from House of Control, so editing them here
 * would be work thrown away. A record registered here is nobody's sync to maintain.
 */
export type SupplierSource = 'house_of_control' | 'manual'

/**
 * What the supplier means for information security — the field the register turns on.
 *
 * A.5.19 does not ask us to assess every supplier with a contract; it concerns those who can reach
 * our information or deliver ICT services. Unassessed is deliberately neither in scope nor out of
 * it: it is unanswered, and that is the finding.
 */
export type SupplierScope = '' | 'ict_service' | 'processes_data' | 'has_access' | 'no_access'

export type SupplierStatus = 'active' | 'dormant' | 'retired'

/** One due-diligence questionnaire held in House of Control. */
export interface SupplierQuestionnaire {
  surveyId: number
  title: string
  responseId?: number
  /** The source's own word — started, submitted, approved. Kept verbatim rather than remapped. */
  status: string
  submittedAt?: string
}

/** The block a sync owns. Empty on a manually registered supplier. */
export interface SupplierMirror {
  sourceId?: string
  orgNumber?: string
  /** House of Control's country field is a nationality dropdown — "Norwegian", not "Norway" —
   * so it is named for what it is and never used as a country of establishment. */
  nationality?: string
  group?: string
  contactEmail?: string
  ourContactPerson?: string
  contractCount: number
  activeContractCount: number
  dueDiligence?: SupplierQuestionnaire[] | null
  lastSyncedAt?: string
  /** Flagged, never deleted: the agreements attached here are evidence about a period of time. */
  missingFromSourceSince?: string
}

export interface Supplier {
  id: string
  title: string
  source: SupplierSource
  mirror: SupplierMirror
  status: SupplierStatus

  scope: SupplierScope
  scopeNote?: string
  scopeSetBy?: string
  scopeSetAt?: string

  ownerRef?: PartyRef
  classification?: string
  /** Ours, and separate from mirror.nationality on purpose. */
  countryOfEstablishment?: string
  orgNumber?: string
  contactEmail?: string
  contactName?: string
  website?: string

  assetIds?: string[] | null
  processingActivityIds?: string[] | null
  documentIds?: string[] | null
  controlIds?: string[] | null
  riskIds?: string[] | null

  review?: {
    cadence?: string
    lastReviewedAt?: string
    lastReviewedBy?: string
    nextReviewDue?: string
  }
  notes?: string
  /** Set when a manual and a mirrored record are confirmed as the same organisation. Both records
   * survive — merging would mean choosing whose assessment to discard. */
  linkedSupplierId?: string
  version: number
}

export interface SupplierAssessment {
  inScope: boolean
  concerns?: string[] | null
  dueDiligenceAnswered: number
  dueDiligenceSent: number
}

export interface SupplierRow {
  supplier: Supplier
  assessment: SupplierAssessment
  hasDpa: boolean
  hasNda: boolean
  sourceLabel: string
  scopeLabel: string
  mirrored: boolean
  registrationNumber?: string
  registrationNumberFrom?: SupplierSource
}

export interface SupplierStats {
  total: number
  bySource: Record<string, number>
  byStatus: Record<string, number>
  byScope: Record<string, number>
  /** The size of the decision nobody has made. The headline. */
  unassessedWithContract: number
  inScope: number
  missingOwner: number
  reviewOverdue: number
  neverReviewed: number
  needsDpa: number
  dueDiligenceUnanswered: number
  missingFromSource: number
  /** Called out because nothing syncs these: if one goes stale, only a person will notice. */
  manuallyRegistered: number
}

export interface SupplierScopeOption {
  value: Exclude<SupplierScope, ''>
  label: string
  inScope: boolean
  needsDpa: boolean
  needsReason: boolean
}

export interface SupplierDuplicate {
  manualId: string
  manualTitle: string
  mirroredId: string
  mirroredTitle: string
}

export interface SupplierList {
  /** The rows in this response — at most `limit` of them. */
  items: SupplierRow[] | null
  count: number
  /** Over everything the filter matched, not over `items`. `stats.total` and `count` differ
   * whenever `truncated` is true, so a page is never presented as the whole answer. */
  stats: SupplierStats
  truncated: boolean
  scopes: SupplierScopeOption[]
  duplicates: SupplierDuplicate[] | null
}

/* ── Supplier scope proposals ───────────────────────────────────────────────── */

/** How much a rule's reasoning rests on the contract category name alone. */
export type ScopeConfidence = 'high' | 'medium' | 'low'

export interface ScopeRule {
  id: string
  categoryPrefix: string
  scope: Exclude<SupplierScope, ''>
  confidence: ScopeConfidence
  /** Written into every supplier's scope note when the rule is accepted, so a decision made in
   * bulk still explains itself on the individual record a year later. */
  reason: string
}

export interface ScopeProposal {
  supplierId: string
  title: string
  ruleId: string
  scope: SupplierScope
  scopeLabel: string
  confidence: ScopeConfidence
  reason: string
  /** What the supplier's contracts are filed under — the evidence the rule fired on. */
  categories?: string[] | null
  activeContractCount: number
}

export interface ScopeProposalGroup {
  rule: ScopeRule
  proposals: ScopeProposal[]
  count: number
}

export interface ScopeProposals {
  groups: ScopeProposalGroup[] | null
  covered: number
  /** Suppliers whose categories support no inference. covered + undecidable = unassessed, so
   * nothing falls through silently. */
  undecidable: ScopeProposal[] | null
  unassessed: number
}

/* ── The management review register (clause 9.3) ────────────────────────────── */

/** One thing a management review has to consider. The labels come from the API so there is one
 * definition of the clause 9.3 list, in Go, in step with the coverage check that measures it. */
export type ReviewInputKind = string

export interface ReviewInputOption {
  kind: ReviewInputKind
  label: string
}

/** One sort of sitting a series has. The management review is an occurrence of the monthly
 * committee, not a second series: modelled as its own series the register would report "0 of 1
 * held" while the committee met twelve times. */
export interface MeetingKind {
  kind: string
  label?: string
  requiredPerYear: number
  requiredInputs?: ReviewInputKind[] | null
  /** Position ids that must be at a sitting of this kind. */
  requiredPositions?: string[] | null
}

export interface MeetingSeries {
  id: string
  name: string
  purpose?: string
  /** With kinds, the server sets this to the sum of theirs. */
  requiredPerYear: number
  chairRef?: PartyRef
  standards?: string[] | null
  requiredInputs?: ReviewInputKind[] | null
  committeeId?: string
  kinds?: MeetingKind[] | null
  active: boolean
  version: number
}

export interface MeetingAttendee {
  ref: PartyRef
  name?: string
  /** Somebody the directory does not know, recorded by the address the attendance report gave. */
  address?: string
  role?: string
  /** For a committee's series this is computed from the committee's required voices, not ticked. */
  required?: boolean
  apology?: boolean
  /** Formally consulted though absent. Never counts towards quorum. */
  consulted?: boolean
}

export interface MeetingInputRecord {
  kind: ReviewInputKind
  /** Empty counts as not covered: ticking a box is not considering something. */
  note?: string
  sources?: string[] | null
}

export type MeetingDecisionStatus = 'open' | 'done' | 'dropped' | 'superseded'

export interface MeetingDecision {
  id: string
  text: string
  ownerRef?: PartyRef
  dueDate?: string
  status: MeetingDecisionStatus
  closedNote?: string
  closedAt?: string
  closedBy?: string
  refs?: string[] | null
  /** What the decision decided, for an AI request: approved, approved_with_conditions, rejected, escalated or handed_over. */
  outcome?: string
}

export interface MeetingMinute {
  id: string
  seriesId: string
  date: string
  title?: string
  /** Which of the series' occurrence kinds this sitting was. */
  kind?: string
  chairRef: PartyRef
  attendees?: MeetingAttendee[] | null
  inputs?: MeetingInputRecord[] | null
  decisions?: MeetingDecision[] | null
  /** Everyone who has written the minute; server-managed. */
  writers?: string[] | null
  notes?: string
  documentId?: string
  /**
   * Each time part of these minutes was drafted from a transcript and a person accepted it. The
   * server owns it: a save from the minutes form neither sends nor drops it.
   */
  draftedFrom?: MinutesDraftSource[] | null
  version: number
}

export interface MeetingCoverage {
  required: ReviewInputKind[] | null
  covered: ReviewInputKind[] | null
  /** Present on the record with nothing written against it. A different failure from never
   * discussed, and a more recoverable one. */
  tickboxed: ReviewInputKind[] | null
  missing: ReviewInputKind[] | null
  extra: ReviewInputKind[] | null
}

export interface MeetingStanding {
  seriesId: string
  name: string
  required: number
  held: number
  /** The number clause 9.3 turns on. */
  shortfall: number
  lastHeld?: string
  /** -1 when never held, which is a different statement from a large number. */
  daysSince: number
  intervalDays: number
  overdue: boolean
  /** Sittings that missed a required input. On-time-but-incomplete satisfies the cadence and not
   * the clause. */
  inputGaps: number
  openDecisions: number
  overdueDecisions: number
  /** Present only for a series that declares occurrence kinds. */
  kinds?: MeetingKindStanding[] | null
  unkindedHeld?: number
}

export interface MeetingKindStanding {
  kind: string
  label?: string
  required: number
  held: number
  shortfall: number
  inputGaps: number
  /** e.g. "management_review: 1 of 4 held". */
  summary: string
}

export interface MeetingRegister {
  series: MeetingStanding[] | null
  behind: number
  /** Worse than being behind: no clause 9.3 review has ever been defined. */
  noManagementReview: boolean
  openDecisions: number
  overdueDecisions: number
}

export interface MinuteRow {
  minute: MeetingMinute
  coverage?: MeetingCoverage
  coverageNote?: string
  seriesName?: string
  /** Computed on read from the committee and the positions register; never stored. */
  quorum?: QuorumEvidence
}

/* ── Committees ─────────────────────────────────────────────────────────────── */

export type SeatCapacity = 'chair' | 'member' | 'secretary'
export type VoiceMode = 'present' | 'present_or_consulted'

export interface CommitteePerson {
  personId: string
  name?: string
}

export interface CommitteeSeat {
  /** Identifies the row across edits; send it back so the API matches the row rather than guessing. */
  id?: string
  positionId: string
  capacity: SeatCapacity
  from?: string
  until?: string
  positionTitle?: string
  holders?: CommitteePerson[] | null
  vacant?: boolean
  inForce?: boolean
}

export interface CommitteeVoice {
  id?: string
  scope: string
  positionId: string
  mode: VoiceMode
  /** Dated like seats: a sitting is judged against the voices in force on its own date. */
  from?: string
  until?: string
  inForce?: boolean
  positionTitle?: string
  holders?: CommitteePerson[] | null
  vacant?: boolean
}

export interface Committee {
  id: string
  name: string
  parentId?: string
  parentName?: string
  mandate?: string
  mandateDocId?: string
  mandateDocTitle?: string
  quorum: { chairRequired: boolean; minOthers: number; from?: string }
  /** Earlier rules, each with the date it took effect. */
  quorumHistory?: { chairRequired: boolean; minOthers: number; from?: string }[] | null
  seats: CommitteeSeat[] | null
  requiredVoices: CommitteeVoice[] | null
  /** Seats are held by position and a person can hold several; quorum counts the people. */
  seatsInForce: number
  peopleSeated: number
  series: { id: string; name: string; requiredPerYear: number; active: boolean; kinds?: MeetingKind[] | null }[] | null
  escalatesTo?: {
    committeeId: string
    name: string
    occurrences?: { seriesId: string; seriesName: string; kind: string }[] | null
  }
  active: boolean
  version: number
}

export interface QuorumResult {
  met: boolean
  chairRequired: boolean
  chairPresent: boolean
  othersNeeded: number
  othersPresent: number
  seatsInForce: number
  peopleSeated: number
  counted?: CommitteePerson[] | null
  unresolved?: string[] | null
  reasons?: string[] | null
}

export interface VoiceResult {
  scope: string
  positionId: string
  mode: VoiceMode
  state: 'present' | 'consulted' | 'missing'
  source: string
  holders?: CommitteePerson[] | null
  note?: string
}

export interface QuorumEvidence {
  committeeId?: string
  quorum?: QuorumResult
  voices?: VoiceResult[] | null
  /** One per attendee on the minute, by index. Required is derived from the committee's voices. */
  attendees?: { index: number; personId?: string; required: boolean; attended: boolean }[] | null
  conflicts?: string[] | null
  met: boolean
  summary: string
}

/* ── Readiness ──────────────────────────────────────────────────────────────── */

/**
 * Where the management system stands, assembled from the same summarisers the register pages use.
 *
 * There is deliberately no overall score. A single percentage over an SoA, a risk register and a
 * document backlog would be a weighted average of incommensurable things, and the weights would be
 * somebody's opinion presented as a measurement.
 */
export interface Readiness {
  generatedAt: string
  soa?: {
    standard: string
    total: number
    applicable: number
    excluded: number
    undecided: number
    auditReady: boolean
    missingJustification: number
    noImplementation: number
    describedNotLinked: number
    noRiskReference: number
    ineffective: number
    undecidedControls?: string[] | null
    noImplementationSample?: string[] | null
  }
  assets?: {
    total: number
    missingOwner: number
    missingTechnicalOwner: number
    missingClassification: number
    overdueReview: number
    neverReviewed: number
    byCriticality?: Record<string, number>
  }
  risks?: {
    total: number
    byBand?: Record<string, number>
    appetiteBreach: number
    needsRescoring: number
    overdueReview: number
    missingOwner: number
    noControlLink: number
  }
  documents?: {
    total: number
    linkedToControl: number
    unlinked: number
    withCandidates: number
    noEqsLink: number
    schedule: CadenceStats
    evidence: EvidenceGaps
  }
  campaigns?: {
    total: number
    open: number
    tasks: number
    tasksSubmitted: number
    campaignsPastEscalation: number
    accessReview: AccessStats
  }
  managementReview?: {
    series: number
    behind: number
    noManagementReviewDefined: boolean
    openDecisions: number
    overdueDecisions: number
    standing?: MeetingStanding[] | null
  }
  processing?: {
    activities: number
    incomplete: number
    lawfulnessConcerns: number
    byArticle?: Record<string, number> | null
    withTransfers: number
    unresolvedStores: number
  }
  reviewQueue?: {
    pending: number
    accepted: number
    rejected: number
    superseded: number
    oldestPendingDays: number
    withQuestions: number
  }
  /** Sections that could not be read. Zero and unknown are different, and only one is good news. */
  unavailable?: string[] | null
  note?: string
}

/* ── Attestation campaigns ─────────────────────────────────────────────────── */

export type CampaignKind =
  | 'asset_attestation' | 'risk_review' | 'access_review'
  | 'control_self_assessment' | 'supplier_review'
export type CampaignStatus = 'draft' | 'open' | 'closed' | 'cancelled'
/**
 * The four answers, and they are peers.
 *
 * Nothing in the interface may make 'confirmed' cheaper to give than the others: if it is, people
 * click it and the register fills with attestations that mean nothing. 'not_mine' especially —
 * ownership is the field most often wrong on an imported register, and the owner is the only person
 * who can say so.
 */
export type Outcome = '' | 'confirmed' | 'corrected' | 'not_mine' | 'cannot_attest'

/**
 * An access review asks a different question, so it takes a different answer.
 *
 * "Correct as recorded" is the wrong vocabulary for access: the reviewer is deciding whether it
 * continues, and keep / modify / revoke is an instruction somebody then has to carry out. The
 * fourth option is not padding — a reviewer who does not know what a grant is for must be able to
 * say so, or they pick "keep" to clear the task and the review becomes evidence of diligence that
 * did not happen.
 */
export type AccessDecision = '' | 'keep' | 'modify' | 'revoke' | 'cannot_say'

/** One grant as it stood when the review was built. Snapshotted, so revoking it — the correct
 * outcome of a review — does not erase what the reviewer was looking at. */
export interface AccessSnapshot {
  assignmentId: string
  personId: string
  upn: string
  name?: string
  /** Three states in two fields: absent from the mirror is not the same as disabled in Entra. */
  inDirectory: boolean
  enabled: boolean
  lastSeenAt?: string
  permission: string
  scopeType?: string
  scopeId?: string
  /** Requires a second administrator to grant. The ones worth reading carefully. */
  privileged: boolean
  grantedBy?: string
  grantedByName?: string
  grantedAt: string
  ageDays: number
  reason?: string
  /** Empty means somebody granted this by hand, which is a different question from a grant that
   * came with a position. */
  conferredByRole?: string
  confirmedBy?: string
  expiresAt?: string
  /** The reviewer granted this themselves. Not refused — with one administrator there would be no
   * review at all — but never hidden. */
  selfGranted?: boolean
}

export interface CampaignItem {
  recordType: string
  recordId: string
  title: string
  summary?: string
  outcome: Outcome
  /** Set instead of outcome on an access review. Only ever one of the two. */
  decision?: AccessDecision
  access?: AccessSnapshot | null
  note?: string
  at?: string
  by?: string
  /** Deciding to remove access and removing it are two events. This is the second. */
  carriedOutAt?: string
  carriedOutBy?: string
  carriedOutNote?: string
}

export interface AccessStats {
  items: number
  decided: number
  keep: number
  modify: number
  revoke: number
  cannotSay: number
  /** Modify and revoke instructions nobody has carried out. The number that says whether the
   * review was a control or a form-filling exercise. */
  outstanding: number
  selfReviewed: number
  privileged: number
}

export interface CampaignTask {
  owner: PartyRef
  items: CampaignItem[] | null
  submittedAt?: string
  submittedBy?: string
}

export interface CampaignStats {
  tasks: number
  tasksSubmitted: number
  items: number
  answered: number
  confirmed: number
  corrected: number
  notMine: number
  cannotAttest: number
  percentAnswered: number
  largestTask: number
  largestTaskOwner?: string
  unanimousConfirmation: boolean
}

export interface CampaignScope {
  categories?: string[] | null
  criticalities?: string[] | null
  ownerIds?: string[] | null
  bands?: string[] | null
  onlyOverdue?: boolean
  describe?: string
}

export interface CampaignRow {
  id: string
  kind: CampaignKind
  title: string
  status: CampaignStatus
  dueDate: string
  cadence: string
  clause: string
  scope?: string
  daysLate: number
  stats: CampaignStats
  myTask?: { items: number; answered: number; submitted: boolean; owner: PartyRef }
}

export interface CampaignList {
  items: CampaignRow[] | null
  count: number
  canAdminister: boolean
  kinds: CampaignKind[] | null
  outcomes: Outcome[] | null
}

export interface CampaignDetail {
  campaign: {
    id: string
    kind: CampaignKind
    title: string
    status: CampaignStatus
    dueDate: string
    cadence: string
    scope: CampaignScope
    clause: string
    openedAt?: string
    closedAt?: string
    version: number
  }
  stats: CampaignStats
  daysLate: number
  myTask: CampaignTask | null
  /** Only one of these two arrives: the campaign's kind decides which. An access reviewer must
   * never be offered "Correct as recorded" — it is not a thing they can say about somebody
   * else's access. */
  outcomes?: { value: Exclude<Outcome, ''>; label: string }[] | null
  decisions?: { value: Exclude<AccessDecision, ''>; label: string; needsReason: string }[] | null
  accessStats?: AccessStats
  canRecordCarriedOut?: boolean
  canAttest: boolean
  canAdminister: boolean
  tasks?: CampaignTask[] | null
  escalated?: { offsetDays: number; to: string; note?: string }[] | null
  evidenceLine?: string
}


/* ── The document register ─────────────────────────────────────────────────── */

export type DocumentKind =
  | 'policy' | 'process' | 'pce' | 'sup' | 'com'
  | 'certificate' | 'checklist' | 'eqs' | 'other'

export interface QmsDocument {
  id: string
  /** The id EQS prints in the document header. Not what the deep link takes. */
  eqsId?: string
  /**
   * What the EQS deep link addresses. Empty until the API supplies it.
   *
   * EQS distinguishes a document from its current revision, and the export carries only the
   * document id — so the link cannot be derived. Building it from the document id produced links
   * that opened the wrong policy, which is worse than no link because somebody follows it.
   */
  revisionId?: string
  url?: string
  title: string
  kind: DocumentKind
  prefix?: string
  status: string
  administrator?: string
  approvedBy?: string
  revision?: string
  validFrom?: string
  /** The date EQS published. Authoritative, and never overwritten by anything here. */
  nextRevision?: string
  /** A review interval set here, never in EQS. Derives a separate date for the documents EQS
   * never scheduled — labelled with its source everywhere it is shown. */
  cadence?: DocumentCadence
  cadenceNote?: string
  cadenceSetBy?: string
  cadenceSetAt?: string
  /** Controls this document evidences: a citation, or a title that is about the control. */
  controlIds?: string[] | null
  /** Proposals from reading the body. Never evidence on their own. */
  candidateControlIds?: string[] | null
  /** Which system this entry came from, and therefore which sync owns it. An unset value means
   * it was recorded here and nothing maintains it. */
  origin?: 'eqs' | 'house_of_control' | 'manual'
  /** Locates the entry in its origin system — a House of Control file id. */
  sourceRef?: string
  sourceFile?: string
  textExtracted: boolean
  pages?: number
}

/** How often a document should be reviewed — set here, never in EQS. */
export type DocumentCadence =
  | '' | 'quarterly' | 'semiannual' | 'annual' | 'biennial' | 'triennial' | 'event_driven'

/**
 * Where a review date came from. Rendered wherever a date is, always — a UI that showed only the
 * date would present a QMS estimate as though EQS had published it.
 */
export type ReviewSource = 'eqs' | 'cadence' | 'event_driven' | 'none'

export interface DocumentSchedule {
  date?: string
  source: ReviewSource
  overdue: boolean
  daysUntil?: number
  /** Always names the source. Show this, not just the date. */
  note: string
}

/** What a document is for, as distinct from what it covers. A control backed only by policy has
 * stated intent; one with evidence has proof it operates. */
export type DocumentClass = 'policy' | 'procedure' | 'evidence' | 'form' | 'unknown'

export interface ControlEvidenceBreakdown {
  controlId: string
  total: number
  byClass: Partial<Record<DocumentClass, number>>
  titles?: Partial<Record<DocumentClass, string[]>>
  /** Every document behind this control is a policy. The list an auditor works through. */
  policyOnly: boolean
  noEvidence: boolean
}

export interface EvidenceGaps {
  controlsWithDocuments: number
  withEvidence: number
  policyOnly: number
  noEvidence: number
  /** Caps how far the numbers above can be trusted. */
  unclassified: number
  byClass: Partial<Record<DocumentClass, number>>
  policyOnlyControls?: string[] | null
}

export interface CadenceStats {
  fromEqs: number
  fromCadence: number
  eventDriven: number
  unscheduled: number
  overdueFromEqs: number
  overdueFromCadence: number
  disagreements: number
  cadenceSetButNoAnchor: number
}

export interface DocumentStats {
  total: number
  byKind: Record<string, number>
  withEqsLink: number
  noEqsLink: number
  noTextExtracted: number
  noReviewDate: number
  reviewOverdue: number
  noApprover: number
  linkedToControl: number
  unlinked: number
  withCandidates: number
}

export interface DocumentList {
  items: QmsDocument[] | null
  count: number
  stats: DocumentStats
  kinds: { kind: DocumentKind; name: string; count: number }[] | null
}

export interface DocumentDetail {
  document: QmsDocument
  controls: { id: string; title: string }[] | null
  candidateControls: { id: string; title: string }[] | null
  reviewOverdue: boolean
  noReviewScheduled: boolean
}

/** The shape the SoA views need: enough to render a link and judge whether it is current. */
export interface DocumentRef {
  id: string
  title: string
  kind: string
  kindName: string
  eqsId?: string
  url?: string
  revision?: string
  validFrom?: string
  nextRevision?: string
  approvedBy?: string
  administrator?: string
  reviewOverdue: boolean
  noReviewScheduled: boolean
}


/* ── The review queue ──────────────────────────────────────────────────────── */

export type ProposalKind =
  | 'processing_activity' | 'asset' | 'asset_fields' | 'document_links' | 'soa_entry' | 'meeting_minutes'
export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'superseded'

/** One field a proposal would alter. Rendered strings: this is read before deciding. */
export interface ProposalChange {
  field: string
  before?: string
  after: string
  note?: string
}

/**
 * Where a proposal came from.
 *
 * A reviewer's first question about a machine-drafted compliance record is "on what basis", and
 * "the model said so" is not an answer. `sources` is what was actually read.
 */
export interface ProposalProvenance {
  actor: string
  actorKind: 'agent' | 'person'
  session?: string
  model?: string
  sources?: string[] | null
}

export interface Proposal {
  id: string
  kind: ProposalKind
  status: ProposalStatus
  targetCollection: string
  targetId?: string
  title: string
  rationale: string
  changes?: ProposalChange[] | null
  /** What the draft could not settle. Answering these is faster than auditing every field. */
  questions?: string[] | null
  provenance: ProposalProvenance
  decidedBy?: string
  decidedAt?: string
  decisionNote?: string
  appliedId?: string
  createdAt: string
  version: number
}

export interface ProposalSummary {
  pending: number
  accepted: number
  rejected: number
  superseded: number
  byKind?: Record<string, number>
  /** How long the most neglected pending item has waited. A queue nobody empties shows here. */
  oldestPendingDays: number
  withQuestions: number
}

export interface ProposalList {
  items: Proposal[] | null
  count: number
  summary: ProposalSummary
  /** Whether this caller may decide anything at all. */
  canDecide: boolean
  /**
   * How many in this list are actually theirs to clear.
   *
   * Separate from canDecide because the two differ for an asset owner, who may decide suggestions
   * filling descriptive fields on their own assets and nothing else. A single boolean would either
   * hide their worklist or promise them the whole queue.
   */
  decidableCount: number
  /** The ids in this list this caller may decide. Per proposal, because decidability is. */
  decidableIds: string[] | null
  kinds: ProposalKind[] | null
  note: string
}

/* ── Minutes drafted from a transcript ──────────────────────────────────────── */

/** Where the meeting was recorded. A reference only: the transcript stays in Teams. */
export interface MinutesSourceRef {
  meetingId: string
  organiser: string
  date: string
  link: string
}

export interface MinutesDraftSource {
  kind: string
  source: MinutesSourceRef
  proposalId: string
  proposedBy: string
  proposedAt: string
  acceptedBy: string
  acceptedByName?: string
  acceptedAt: string
  /** What the minute actually received; items already there unchanged are in merge.alreadyRecorded. */
  attendees: number
  inputs: number
  decisions: number
  merge?: MergeSummary
}

export interface MergeSummary {
  attendeesAdded: number
  attendeesChanged: number
  inputsAdded: number
  inputsReplaced: number
  inputsExtended: number
  decisionsAdded: number
  alreadyRecorded: number
  kindAssigned?: boolean
  createdMinute: boolean
}

export interface DraftAttendee {
  upn: string
  name?: string
  /** What the server resolved the address to; an empty id means nobody in the directory. */
  ref: PartyRef
  joinedAt?: string
  leftAt?: string
  /** Somebody absent whom chat or the transcript suggests sent apologies. Needs confirming. */
  apologySuggested?: boolean
  apologyEvidence?: string
}

export interface DraftInput {
  kind: ReviewInputKind
  note: string
  /** Where in the transcript the note came from. */
  passage: string
  sources?: string[] | null
}

export interface DraftDecision {
  text: string
  passage: string
  ownerUpn?: string
  owner: PartyRef
  dueDate?: string
  refs?: string[] | null
  /** Suggested by the draft; written only if the reviewer confirms or chooses it. */
  outcome?: string
}

export interface MinutesDraft {
  seriesId: string
  occurrence: { date: string; kind?: string }
  sourceRef: MinutesSourceRef
  attendees: DraftAttendee[] | null
  inputs: DraftInput[] | null
  decisions: DraftDecision[] | null
}

export interface MinutesSelection {
  attendees: { index: number; accept: boolean; confirmApology?: boolean }[]
  /** text is sent only when edited; an empty text is refused, never read as "use the draft's". */
  inputs: { index: number; accept: boolean; text?: string; sources?: string[]; overwrite?: boolean }[]
  /** ownerUpn, dueDate and text are sent only when changed; an empty owner or date clears it. refs is what is written. */
  decisions: {
    index: number; accept: boolean; text?: string; ownerUpn?: string; dueDate?: string; refs?: string[]
    /** The outcome the reviewer chose, or '' for none. confirmOutcome takes the draft's suggestion as it is. */
    outcome?: string; confirmOutcome?: boolean
  }[]
  chairPersonId?: string
  /** Confirms giving minutes recorded without a kind the draft's kind. */
  confirmKind?: boolean
  /** Confirms creating a new minute although another sitting of the series has minutes that day. */
  confirmSecondSitting?: boolean
  /** The version of the existing minute the reviewer looked at; 0 when there was none. */
  minuteVersion?: number
}

export interface MinutesReview {
  draft: MinutesDraft
  seriesName: string
  committeeId?: string
  /** True when Required comes from the committee or the kind rather than being ticked. */
  requiredDerived: boolean
  existing: { minute: MeetingMinute } | null
  /** Who may accept these minutes: the committee's chair and secretary on the sitting date. */
  officers: CommitteePerson[] | null
  chairCandidates: CommitteePerson[] | null
  /** The one chair-seat holder who is on the attendance report as present; empty otherwise. */
  chairSuggestion?: string
  needsChair: boolean
  /** The series belongs to another committee now than when this was drafted; it cannot be accepted. */
  committeeMoved?: boolean
  /** Other sittings of the series with minutes on the same day, of any kind. */
  sameDay: { id: string; kind?: string; version: number }[] | null
  /** The existing minutes have no kind and accepting would give them the draft's. */
  kindWillBeSet?: boolean
  mayDecide: boolean
  /** May reject: the officers, or the meeting writers when no seat was in force. */
  mayReject?: boolean
  whyNot?: string
}

export interface MinutesPreview {
  ok: boolean
  problems: string[] | null
  minute?: MeetingMinute
  quorum?: QuorumEvidence
  coverage?: MeetingCoverage
  coverageNote?: string
  outcome?: {
    attendees: number
    inputs: number
    decisions: number
    /** Draft attendee index to its row on the minute, keyed by the index as a string. */
    attendeeAt: Record<string, number> | null
    merge?: MergeSummary
    notes: string[] | null
  }
  /** Draft attendee index to whether the committee requires them, keyed by the index as a string. */
  required?: Record<string, boolean> | null
}

export interface ProposalDetail {
  proposal: Proposal
  diff: string[] | null
  canDecide: boolean
  /** Present for a minutes proposal: the draft, the minute on the books and who may accept. */
  minutes?: MinutesReview
  /** Why the review pane is missing, when it is. */
  minutesError?: string
  /**
   * Why this caller may not decide it. The drafter is refused even holding the role, and an asset
   * owner is refused for fields that change who is accountable or what the record claims to cover.
   */
  whyNot?: string
}

/**
 * The notification feed.
 *
 * Derived server-side on every request from the registers, never stored. So the items in a feed
 * are true at the moment it was fetched and there is no "stale notification" state to handle in
 * the client — refetch and the list is correct again.
 */
export type NotificationSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export type NotificationItem = {
  key: string
  category: string
  severity: NotificationSeverity
  title: string
  detail: string
  to: string
  dueOn?: string
  /** True when the caller owns the record; false when their role makes it their problem. */
  mine: boolean
  read: boolean
  dismissed: boolean
}

export type NotificationFeed = {
  items: NotificationItem[]
  unread: number
  total: number
  truncated: boolean
  asOf: string
  /** What could not be read. An empty feed and a broken feed must not look the same. */
  notes?: string[]
}

// ---- AI classification records ---------------------------------------------------------------
// Design: qms-docs docs/18-ai-governance-intake.md. Everything below is what qms-api serves; the
// tier, the obligations and the stale and breached flags are computed there and only shown here.

export type ClassTier = 'prohibited' | 'high_risk' | 'transparency' | 'minimal'
export type ClassStatus = 'draft' | 'in_review' | 'approved' | 'rejected' | 'superseded'
export type ClassCell = 'author' | 'ai_committee' | 'data_protection'
export type ConditionStatus = 'confirmed' | 'unconfirmed' | 'overdue' | 'breached'
export type Cadence = 'monthly' | 'quarterly' | 'semiannual' | 'annual'

export interface ClassPractice { key: string; label: string }
export interface ClassRowAnswer { key: string; applies: boolean | null; reason: string }
export interface ClassObligation { id: string; text: string; generated: boolean }

export interface ClassCatalogue {
  art5: ClassPractice[]
  annexI: ClassPractice
  annexIII: ClassPractice[]
  transparency: ClassPractice
  obligations: Record<ClassTier, ClassObligation[]>
  art4Obligations: ClassObligation[]
  autonomyLevels: string[]
  targetUsers: string[]
  actRoles: string[]
  gpaiPositions: string[]
  gdprRoles: string[]
  cadences: Cadence[]
}

export interface ClassVerdict {
  tier: ClassTier | ''
  decided: boolean
  hardStop: boolean
  step: number
  prohibited: { key: string; label: string; reason: string }[]
  unanswered: string[]
  summary: string
}

export interface ClassApproval {
  cell: ClassCell
  signedBy: string
  name?: string
  signedAt: string
  meeting?: { seriesId: string; minuteId: string; decisionId: string } | null
  note?: string
}

export interface ClassControlLink { soaControlId: string; riskId: string; note: string; mustNotRemove: boolean }
export interface ClassOpenItem {
  id: string
  text: string
  done: boolean
  doneBy?: string
  doneAt?: string | null
  /** Closed without being done. Kept on the record, with who, when and why. */
  withdrawn: boolean
  withdrawnReason?: string
  withdrawnBy?: string
  withdrawnAt?: string | null
}
export interface ClassConfirmation { by: string; byName?: string; at: string }

export interface ClassRecord {
  id: string
  recordId: string
  assetId: string
  number: number
  status: ClassStatus
  lane: { customerData: boolean; personalData: boolean; telemetry: boolean }
  verdict: ClassVerdict
  precedent: boolean
  precedentNote: string
  description: string
  negativeClaims: string[]
  autonomyLevel: string
  targetUsers: string[]
  toolsMayCall: string[]
  repository: {
    url: string
    urlConfirmed: ClassConfirmation | null
    aiReady: { passed: boolean; note: string; by: ClassConfirmation } | null
  }
  roles: {
    actRoles: string[]; actReasoning: string
    gpaiPosition: string; gpaiReasoning: string
    gdprRole: string; gdprReasoning: string
  }
  data: {
    sent: string[]; notSent: string[]; personalDataExposure: string
    subProcessors: { name: string; location: string; transferBasis: string }[]
  }
  cascade: { art5: ClassRowAnswer[]; annexI: ClassRowAnswer; annexIII: ClassRowAnswer[]; transparency: ClassRowAnswer }
  art4: { generalStaffTraining: boolean; specialisedTraining: boolean }
  obligations: ClassObligation[]
  controls: ClassControlLink[]
  residualRisk: string
  reAuditDue: string | null
  openItems: ClassOpenItem[]
  release: { gate: string; featureFlag: string }
  approvals: ClassApproval[]
  approvedAt: string | null
  rejectReason: string
  authorId: string
  updatedAt: string
  version: number
}

export interface ClassApprovedSummary {
  number: number; tier: ClassTier; actRoles: string[]; approvedAt: string | null; reAuditDue: string | null
}

/** An Art. 5 finding on a version newer than the approved one. While it stands the record reads prohibited. */
export interface ClassFinding {
  number: number
  practices: { key: string; label: string; reason: string }[]
  at: string | null
  by: string
  summary: string
}

export interface ClassStanding {
  total: number; overdue: number; unconfirmed: number; breached: number
  stale: boolean; needsReassessment: boolean; nextDue: string | null
  finding: ClassFinding | null
}

export interface ClassRow {
  recordId: string
  assetId: string
  assetTitle: string
  headNumber: number
  status: ClassStatus
  tier: ClassTier | ''
  precedent: boolean
  actRoles: string[]
  reAuditDue: string | null
  approved: ClassApprovedSummary | null
  standing: ClassStanding
  updatedAt: string
}

export interface BreachEvent {
  at: string
  by: string
  note: string
  dismissedAt?: string | null
  dismissedBy?: string
  dismissReason?: string
  /** Answered by approving a version created after it: when, and by which version. Never rewritten. */
  clearedAt?: string | null
  clearedByVersion?: number
}

export interface BoundaryCondition {
  id: string
  recordId: string
  claim: string
  consequence: string
  owner: { type: 'person' | 'position'; id: string; display?: string }
  cadence: Cadence
  lastConfirmedAt: string | null
  lastConfirmedBy?: string
  lastConfirmedNote?: string
  /** Every report, oldest first. A repeat report is added beside the first, never over it. */
  breaches: BreachEvent[]
  retired: boolean
  retiredReason?: string
  version: number
  status: ConditionStatus
  nextDue: string
}

/** What a condition's owner sees, who may not be able to read the record it belongs to. */
export interface ConditionDetail extends BoundaryCondition {
  assetId: string
  assetTitle: string
  canConfirm: boolean
  canReportBreach: boolean
  canDismiss: boolean
  canReadRecord: boolean
}

export interface ClassDetail {
  record: ClassRecord
  versions: { number: number; id: string; status: ClassStatus; tier: ClassTier | ''; approvedAt: string | null }[]
  approved: ClassApprovedSummary | null
  conditions: BoundaryCondition[]
  standing: ClassStanding
  warnings: string[]
  assetTitle: string
  /** May write to the record at all (the reviewer, or the asset's owner), whatever its status. */
  canWrite: boolean
  /** May write to it now: canWrite and the record is a draft. */
  canEdit: boolean
  canReview: boolean
  isHead: boolean
  protectedControlsDropped?: ClassControlLink[]
  /** Set when the record was saved but the asset could not be linked to it. */
  linkWarning?: string
  /** Everyone who signed a cell on any version of the record; they may confirm its conditions. */
  approverIds: string[]
  finding: ClassFinding | null
}

/** What an asset reports about its record. The asset's own tier and role are read from it. */
export interface AssetClassification {
  recordId: string
  headStatus: ClassStatus
  approved: ClassApprovedSummary | null
  stale: boolean
  needsReassessment: boolean
}


// ---- AI intake cases -----------------------------------------------------------------------
// Design: qms-docs docs/18-ai-governance-intake.md. The stage, the clocks, who may act and what an
// approval deposited are qms-api's; the pages only show them and offer what the API will accept.

export type IntakeMode = 1 | 2 | 3 | 4 | 5 | 6
export type IntakeStage =
  | 'submitted' | 'pre_triage' | 'out_of_scope' | 'fast_track' | 'in_depth' | 'committee_decision'
  | 'approved' | 'approved_with_conditions' | 'escalated' | 'rejected' | 'handed_over'
export type IntakeTrack = 'ai_architecture_safety' | 'infosec_infra' | 'privacy_legal_ai_act'
export type YesNoUnsure = 'yes' | 'no' | 'unsure' | ''

export interface IntakeCatalogue {
  modes: { mode: IntakeMode; label: string; initiative: string; asksLane: boolean; fastTrackAllowed: boolean }[]
  art5: ClassPractice[]
  tracks: { track: IntakeTrack; label: string }[]
  autonomyLevels: string[]
  targetUsers: string[]
  slas: { preTriageBusinessDays: number; trackBusinessDays: number; decisionLogHours: number }
}

export interface IntakeSla {
  kind: 'pre_triage' | 'track' | 'decision_logged'
  track?: IntakeTrack
  label: string
  startedAt: string
  dueAt: string
  doneAt: string | null
  state: 'running' | 'met' | 'overdue' | 'late'
}

export interface IntakeScope { type: 'group' | 'orgUnit' | 'person'; orgUnitIds: string[]; personIds: string[] }

/** What a submission carries. Only the section for the chosen mode is filled. */
export interface IntakeSubmit {
  mode: IntakeMode
  title?: string
  orgUnitId?: string
  sponsorId?: string
  techLeadId?: string
  targetDate?: string
  problem?: string
  lane?: { touchesData: YesNoUnsure; note?: string }
  art5?: string[]
  art5Doubt?: string
  build?: Record<string, unknown>
  buy?: Record<string, unknown>
  tool?: { name: string; link: string; useCase: string }
  request?: { toolId: string; scope: IntakeScope; why: string }
  model?: Record<string, unknown>
  saas?: Record<string, unknown>
}

export interface IntakeKind { kind: 'entitlement' | 'approval_change' | 'already_covered' | 'to_be_checked'; message: string }

export interface IntakeTrackState {
  track: IntakeTrack
  reviewerId: string
  reviewerName: string
  assignedAt: string
  previousReviewers: string[]
  finding: { outcome: string; note: string; by: string; byName: string; at: string } | null
}

export interface IntakeObligation {
  id: string
  text: string
  owner: { type: string; id: string }
  due: string
  done: boolean
  doneNote: string
  state: 'open' | 'overdue' | 'done'
}

export interface IntakeOutput { kind: string; id: string; note: string; created: boolean }

export interface IntakeDecision {
  override: { reason: string; previousStatus: string; previousRoutes: string[] | null; previousConditions: string } | null
  handedTo: string
  outcome: IntakeStage
  via: 'fast_track' | 'committee'
  byName: string
  at: string
  reason: string
  feedback: string
  meeting: { seriesId: string; minuteId: string; decisionId: string } | null
  decisionText: string
  quorum: string
  escalatedTo: { committeeId: string; name: string } | null
  expiresAt: string | null
  monitoring: { cadence: string; nextDue: string } | null
}

export interface IntakeCase {
  id: string
  mode: IntakeMode
  modeLabel: string
  stage: IntakeStage
  header: { title: string; sponsorId: string; sponsorName: string; techLeadId: string; techLeadName: string; orgUnitId: string; targetDate: string; problem: string }
  lane: { touchesData: YesNoUnsure; note: string }
  art5: { doubt: string }
  build: Record<string, unknown> | null
  buy: Record<string, unknown> | null
  tool: { name: string; link: string; useCase: string } | null
  request: { toolId: string; scope: IntakeScope; why: string; kind: string; message: string } | null
  model: Record<string, unknown> | null
  saas: Record<string, unknown> | null
  triage: {
    byName: string; at: string; auto: boolean; lane: IntakeStage; reason: string; touchesData: string; destination: string
    art4: { generalStaffTraining: boolean; specialisedTraining: boolean }; art4Set: boolean; spawnMode2: boolean
    submitterTouches: string; overrode: boolean
  } | null
  tracks: IntakeTrackState[]
  pending: { by: string; byName: string; claimedAt: string; needsAttention: string; input: { Outcome: IntakeStage } } | null
  decision: IntakeDecision | null
  conditions: IntakeObligation[]
  outputs: IntakeOutput[]
  log: { at: string; byName: string; from: string; to: string; note: string }[]
  spawnedFrom: string
  requesterId: string
  requesterName: string
  submittedAt: string
  closedAt: string | null
  version: number
  slas: IntakeSla[]
  overdue: boolean
  can: {
    triage: boolean
    reassign: boolean
    recordFinding: IntakeTrack[]
    reroute: boolean
    releaseClaim: boolean
    canSetVendor: boolean
    approveCommittee: boolean
    decideFastTrack: boolean
    decideCommittee: boolean
    completeConditions: string[]
  }
  requestNow: IntakeKind | null
}

export interface IntakeRow {
  id: string
  mode: IntakeMode
  modeLabel: string
  title: string
  stage: IntakeStage
  orgUnitId: string
  requesterName: string
  submittedAt: string
  overdue: boolean
  nextDue: string | null
  slas: IntakeSla[]
  openConditions: number
  overdueConditions: number
  touchesData: string
}

export interface IntakeTriageBody {
  lane: 'out_of_scope' | 'fast_track' | 'in_depth'
  reason: string
  touchesData?: string
  destination?: string
  art4?: { generalStaffTraining: boolean; specialisedTraining: boolean }
  reviewers?: Partial<Record<IntakeTrack, string>>
  spawnMode2?: boolean
  supplierId?: string
  supplierName?: string
  version: number
}

export interface IntakeDecisionBody {
  outcome: IntakeStage
  reason: string
  feedback?: string
  meeting?: { seriesId: string; minuteId: string; decisionId: string }
  expiresAt?: string
  scopeOrgUnitIds?: string[]
  monitoring?: string
  tool?: Record<string, unknown>
  conditions?: { text: string; ownerType: string; ownerId: string; due: string }[]
  groupWide?: boolean
  overrideProhibited?: boolean
  version: number
}

export const api = {
  me: () => request<Me>('/api/v1/me'),

  notifications: () => request<NotificationFeed>('/api/v1/notifications'),
  /** Where the management system stands, in one request. */
  readiness: () => request<Readiness>('/api/v1/readiness'),

scopeProposals: () => request<ScopeProposals>('/api/v1/suppliers/scope-proposals'),
  /** Accept one rule for all its suppliers, in one audited act that names the rule. */
  acceptScopeRule: (rule: string, body: { supplierIds?: string[]; note?: string; reason?: string } = {}) =>
    request<{ rule: string; scope: string; applied: number; reason: string; note?: string; failed?: Record<string, string> }>(
      `/api/v1/suppliers/scope-proposals/${encodeURIComponent(rule)}/accept`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  suppliers: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<SupplierList>(`/api/v1/suppliers${qs(p)}`),
  supplier: (id: string) =>
    request<SupplierRow & { scopes: SupplierScopeOption[] }>(
      `/api/v1/suppliers/${encodeURIComponent(id)}`,
    ),
  /** Register a supplier by hand. Marked as registered here, and no sync will ever overwrite it. */
  registerSupplier: (body: Record<string, unknown>) =>
    request<{ supplier: Supplier; sourceLabel: string; note: string }>('/api/v1/suppliers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchSupplier: (id: string, body: Record<string, unknown>) =>
    request<{ supplier: Supplier; assessment: SupplierAssessment }>(
      `/api/v1/suppliers/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  /** Confirm a manual and a mirrored record are the same organisation. Both records survive. */
  linkSuppliers: (id: string, body: { otherId: string; version?: number; reason?: string }) =>
    request<{ linked: string[] }>(`/api/v1/suppliers/${encodeURIComponent(id)}/link`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  meetingRegister: () =>
    request<{ register: MeetingRegister; managementReviewInputs: ReviewInputOption[] }>(
      '/api/v1/meetings',
    ),
  meetingSeries: (includeInactive = false) =>
    request<{ items: MeetingSeries[] | null; count: number }>(
      `/api/v1/meetings/series${includeInactive ? '?includeInactive=true' : ''}`,
    ),
  saveMeetingSeries: (body: Record<string, unknown>) =>
    request<MeetingSeries>('/api/v1/meetings/series', { method: 'PUT', body: JSON.stringify(body) }),
  minutes: (p: Record<string, string | number | undefined> = {}) =>
    request<{ items: MinuteRow[] | null; count: number }>(`/api/v1/meetings/minutes${qs(p)}`),
  committees: (includeInactive = false) =>
    request<{ items: Committee[] | null; count: number }>(
      `/api/v1/committees${includeInactive ? '?includeInactive=true' : ''}`,
    ),
  committee: (id: string) => request<Committee>(`/api/v1/committees/${encodeURIComponent(id)}`),
  saveCommittee: (body: Record<string, unknown>) =>
    request<Committee>('/api/v1/committees', { method: 'PUT', body: JSON.stringify(body) }),
  saveMinute: (body: Record<string, unknown>) =>
    request<{ minute: MeetingMinute; coverage?: MeetingCoverage; coverageNote?: string; quorum?: QuorumEvidence }>(
      '/api/v1/meetings/minutes',
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  closeMeetingDecision: (
    minuteId: string,
    body: { decisionId: string; status: MeetingDecisionStatus; note: string; version?: number; reason?: string },
  ) =>
    request<MeetingMinute>(
      `/api/v1/meetings/minutes/${encodeURIComponent(minuteId)}/close-decision`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Records that an access-review decision was actually performed — a different act, by a
   * different person, from deciding it. */
  campaignCarriedOut: (
    id: string,
    body: { recordId: string; note?: string; version?: number; reason?: string },
  ) =>
    request<{ campaign: CampaignRow; accessStats: AccessStats }>(
      `/api/v1/campaigns/${encodeURIComponent(id)}/carried-out`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** The one write the document register accepts. It never touches the EQS date. */
  setDocumentCadence: (
    id: string,
    body: { cadence: DocumentCadence; note?: string; version?: number; reason?: string },
  ) =>
    request<{ document: QmsDocument; schedule: DocumentSchedule; warning?: string }>(
      `/api/v1/documents/${encodeURIComponent(id)}/cadence`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  /** Mark specific items read, or everything currently in the feed. */
  markNotificationsRead: (body: { keys?: string[]; all?: boolean }) =>
    request<{ read: number; dismissed: number }>('/api/v1/notifications/read', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Dismiss one item. There is no dismiss-all: the server refuses it. */
  dismissNotification: (key: string) =>
    request<{ read: number; dismissed: number }>('/api/v1/notifications/dismiss', {
      method: 'POST',
      body: JSON.stringify({ keys: [key] }),
    }),
  restoreNotifications: () =>
    request<{ dismissed: number }>('/api/v1/notifications/restore', { method: 'POST' }),
  version: () => request<{ service: string; version: string; env: string }>('/version'),

  assets: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<{ items: Asset[]; count: number; limit: number; offset: number }>(
      `/api/v1/assets${qs(p)}`,
    ),
  asset: (id: string) => request<Asset>(`/api/v1/assets/${encodeURIComponent(id)}`),
  assetInstances: (id: string) =>
    request<{ drillDown: DrillDown; items: Instance[]; count: number; rollup: InstanceRollup; reason?: string }>(
      `/api/v1/assets/${encodeURIComponent(id)}/instances`,
    ),
  assetStats: () => request<AssetStats>('/api/v1/assets/stats'),

  setClassification: (
    id: string,
    body: { classification: string; cia: { c: number; i: number; a: number }; reason: string; version?: number },
  ) =>
    request<Asset>(`/api/v1/assets/${encodeURIComponent(id)}/classification`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  createAsset: (body: Record<string, unknown>) =>
    request<Asset>('/api/v1/assets', { method: 'POST', body: JSON.stringify(body) }),

  patchAsset: (id: string, body: Record<string, unknown>) =>
    request<Asset>(`/api/v1/assets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  createRisk: (body: Record<string, unknown>) =>
    request<{ risk: Risk }>('/api/v1/risks', { method: 'POST', body: JSON.stringify(body) }),

  patchRisk: (id: string, body: Record<string, unknown>) =>
    request<{ risk: Risk; needsRescoring: boolean }>(`/api/v1/risks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  attest: (id: string, body: { accurate: boolean; note?: string }) =>
    request<{ id: string; review: Review }>(`/api/v1/assets/${encodeURIComponent(id)}/attest`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  risks: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<{ items: Risk[]; count: number }>(`/api/v1/risks${qs(p)}`),
  risk: (id: string) =>
    request<{ risk: Risk; needsRescoring: boolean; rescoringNote: string }>(
      `/api/v1/risks/${encodeURIComponent(id)}`,
    ),
  /* The review queue: agent drafts, human decisions. */
  proposals: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<ProposalList>(`/api/v1/proposals${qs(p)}`),
  proposal: (id: string) => request<ProposalDetail>(`/api/v1/proposals/${encodeURIComponent(id)}`),
  minutesPreview: (id: string, selection: MinutesSelection) =>
    request<MinutesPreview>(`/api/v1/proposals/${encodeURIComponent(id)}/minutes-preview`,
      { method: 'POST', body: JSON.stringify(selection) }),
  decideProposal: (id: string, action: 'accept' | 'reject' | 'withdraw', body: Record<string, unknown>) =>
    request<{ proposal: Proposal; appliedId?: string }>(
      `/api/v1/proposals/${encodeURIComponent(id)}/${action}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /* The document register — read-only; EQS is where documents are written. */
  documents: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<DocumentList>(`/api/v1/documents${qs(p)}`),
  document: (id: string) =>
    request<DocumentDetail>(`/api/v1/documents/${encodeURIComponent(id)}`),

  /* Attestation campaigns. */
  campaigns: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<CampaignList>(`/api/v1/campaigns${qs(p)}`),
  campaign: (id: string) => request<CampaignDetail>(`/api/v1/campaigns/${encodeURIComponent(id)}`),
  createCampaign: (body: Record<string, unknown>) =>
    request<{ campaign: CampaignRow; stats: CampaignStats; unownedRecords: string[] | null }>(
      '/api/v1/campaigns', { method: 'POST', body: JSON.stringify(body) },
    ),
  campaignAction: (id: string, action: 'open' | 'close' | 'cancel', body: Record<string, unknown>) =>
    request<{ campaign: CampaignRow; stats: CampaignStats; evidenceLine?: string; nextDueDate?: string }>(
      `/api/v1/campaigns/${encodeURIComponent(id)}/${action}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  attestCampaign: (id: string, body: Record<string, unknown>) =>
    request<{ myTask: CampaignTask; stats: CampaignStats; accessStats?: AccessStats }>(
      `/api/v1/campaigns/${encodeURIComponent(id)}/attest`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  campaignEscalations: () =>
    request<{ items: unknown[] | null; count: number; note: string }>('/api/v1/campaigns/escalations'),

  /* GDPR Article 30 record. */
  ropa: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<RoPAList>(`/api/v1/ropa${qs(p)}`),
  ropaActivity: (id: string) =>
    request<RoPADetail>(`/api/v1/ropa/${encodeURIComponent(id)}`),
  patchRopaActivity: (id: string, body: Record<string, unknown>) =>
    request<{ activity: ProcessingActivity; assessment: Article30Assessment }>(
      `/api/v1/ropa/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  putRopaRegister: (role: string, body: Record<string, unknown>) =>
    request<{ register: RoPARegister }>(`/api/v1/ropa/registers/${encodeURIComponent(role)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  ropaExport: (role = 'processor') => request<RoPAExport>(`/api/v1/ropa/export${qs({ role })}`),

  soaEntry: (controlId: string) =>
    request<SoAEntryDetail>(`/api/v1/soa/${encodeURIComponent(controlId)}`),
  patchSoaEntry: (controlId: string, body: Record<string, unknown>) =>
    request<{ entry: SoAEntry }>(`/api/v1/soa/${encodeURIComponent(controlId)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  /**
   * Confirm a suggested document as evidence for a control.
   *
   * Writes both halves of "where is the proof": the line's own citation, and the document's
   * control link. Its own call rather than a field on patchSoaEntry because it touches the
   * document register too, and the two can fail separately — a 409 here can mean the citation
   * landed and the link did not, which the message says.
   */
  confirmSoaEvidence: (controlId: string, body: Record<string, unknown>) =>
    request<{ controlId: string; citation: string; document: DocumentRef }>(
      `/api/v1/soa/${encodeURIComponent(controlId)}/evidence/documents`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * The published AI tooling list: approved, conditionally approved and withdrawn tools, with
   * the supplier, model family names and org unit names needed to label them. Readable by anyone
   * signed in, unlike the asset register, which sends the AI facet only to operational readers.
   */
  aiTools: () => request<AiToolList>('/api/v1/ai-tools'),

  /** What the caller may use right now. Any signed-in user; it is about themselves. */
  aiToolsMine: () => request<AiToolsMine>('/api/v1/ai-tools/mine'),

  /**
   * Entitlements, with their computed status. Names people, so the API refuses everyone but approvers
   * and auditors; a 403 here is the answer, not an error to retry.
   */
  aiEntitlements: (f: { tool?: string; orgUnit?: string; person?: string; state?: 'active' | 'ended' } = {}) =>
    request<EntitlementList>(`/api/v1/ai-entitlements${qs(f)}`),

  /** Grants one entitlement per person when several people are named. Needs ai_tool_approval. */
  grantAiEntitlement: (body: GrantEntitlementBody) =>
    request<GrantResult>('/api/v1/ai-entitlements', {
      method: 'POST', body: JSON.stringify(body),
    }),

  revokeAiEntitlement: (id: string, reason: string) =>
    request<{ entitlement: EntitlementRecord }>(`/api/v1/ai-entitlements/${encodeURIComponent(id)}/revoke`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),

  /** People the grant form may offer: enabled accounts only. Needs ai_tool_approval, not admin. */
  aiEntitlementPeople: (q: string) =>
    request<{ items: PersonChoice[] | null }>(`/api/v1/ai-entitlements/people${qs({ q })}`),

  riskHistory: (id: string) =>
    request<RiskHistory>(`/api/v1/risks/${encodeURIComponent(id)}/history`),

  riskStats: () => request<RiskStats>('/api/v1/risks/stats'),

  // The published risk criteria. Fetched rather than hardcoded so the grid a user
  // clicks is the same one that bands the register — a matrix drawn in the frontend
  // can disagree with the scoring function, and the coloured square would win.
  riskMatrix: (type?: 'threat' | 'opportunity') =>
    request<RiskMatrix>(`/api/v1/risk-matrix${qs({ type })}`),

  /** Proposals only — nothing is written, and none of them carries a rating. */
  suggestRisks: (assetId: string, max = 6) =>
    request<SuggestionResult>(`/api/v1/assets/${encodeURIComponent(assetId)}/suggest-risks${qs({ max })}`,
      { method: 'POST' }),

  checklists: () => request<{ templates: ChecklistInfo[] }>('/api/v1/checklists'),

  checkRuns: (p: Record<string, string | number | undefined> = {}) =>
    request<{ items: { run: CheckRun; summary?: CheckSummary; canEdit: boolean }[]; count: number }>(
      `/api/v1/checks${qs(p)}`),

  checkRun: (id: string) => request<CheckRunDetail>(`/api/v1/checks/${encodeURIComponent(id)}`),

  startCheck: (body: Record<string, unknown>) =>
    request<CheckRunDetail>('/api/v1/checks', { method: 'POST', body: JSON.stringify(body) }),

  /** Sparse save: one item at a time is normal, because this is filled in while walking. */
  saveCheck: (id: string, body: Record<string, unknown>) =>
    request<{ run: CheckRun; summary: CheckSummary }>(`/api/v1/checks/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  orgRefs: (kind: OrgRefKind) => request<OrgRefList>(`/api/v1/org-refs/${kind}`),
  saveOrgRefs: (kind: OrgRefKind, entries: OrgRefEntry[], reason: string) =>
    request<OrgRefList>(`/api/v1/org-refs/${kind}`,
      { method: 'PUT', body: JSON.stringify({ entries, reason }) }),

  categories: () => request<{ categories: CategoryEntry[] }>('/api/v1/categories'),
  saveCategories: (categories: CategoryEntry[], reason: string) =>
    request<{ categories: CategoryEntry[]; version: number; message?: string }>('/api/v1/categories',
      { method: 'PUT', body: JSON.stringify({ categories, reason }) }),

  // The review gate: what a connector found, before any of it becomes a record.
  findings: (p: Record<string, string | number | undefined> = {}) =>
    request<{ groups: FindingGroup[]; count: number }>(`/api/v1/review/findings${qs(p)}`),
  findingInstances: (source: string, findingKey: string, limit = 500) =>
    request<{ items: Record<string, unknown>[]; count: number }>(
      `/api/v1/review/findings/instances${qs({ source, findingKey, limit })}`,
    ),
  discoveries: (all = false) =>
    request<{ items: Discovery[]; count: number }>(`/api/v1/review/discoveries${qs({ all })}`),
  promoteDiscovery: (id: string, body: Record<string, unknown>) =>
    request<{ asset: Asset; message: string }>(
      `/api/v1/review/discoveries/${encodeURIComponent(id)}/promote`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  dismissDiscovery: (id: string, reason: string) =>
    request<{ id: string; state: string }>(
      `/api/v1/review/discoveries/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  allowances: () =>
    request<{ items: AllowanceRow[]; count: number; stats: Record<string, number> }>('/api/v1/allowances'),
  proposeAllowance: (body: Record<string, unknown>) =>
    request<{ allowance: Allowance; message: string }>('/api/v1/allowances',
      { method: 'POST', body: JSON.stringify(body) }),
  approveAllowance: (id: string, note?: string) =>
    request<{ allowance: Allowance }>(`/api/v1/allowances/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: JSON.stringify({ note }) }),
  revokeAllowance: (id: string, reason: string) =>
    request<{ allowance: Allowance; message: string }>(
      `/api/v1/allowances/${encodeURIComponent(id)}/revoke`,
      { method: 'POST', body: JSON.stringify({ reason }) }),

  roleCatalogue: () =>
    request<{ roles: PermissionInfo[]; scopeTypes: ScopeTypeOption[] }>('/api/v1/admin/permissions'),

  positions: (q = '', limit = 200, all = false) =>
    request<{ items: RoleRow[]; count: number; stats: Record<string, number> }>(
      `/api/v1/roles${qs({ q, limit, all })}`,
    ),
  createPosition: (body: Record<string, unknown>) =>
    request<{ position: Role }>('/api/v1/roles', { method: 'POST', body: JSON.stringify(body) }),
  updatePosition: (id: string, body: Record<string, unknown>) =>
    request<{ position: Role; message?: string }>(`/api/v1/roles/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  appoint: (id: string, body: Record<string, unknown>) =>
    request<{
      position: Role; display: string; message?: string
      rolesGranted?: string[]; rolesPendingConfirmation?: string[]; rolesRefused?: string[]
    }>(`/api/v1/roles/${encodeURIComponent(id)}/appoint`,
      { method: 'POST', body: JSON.stringify(body) }),
  archiveRole: (id: string, reason: string) =>
    request<{ position: Role; archivedRecords?: string[]; message?: string }>(
      `/api/v1/roles/${encodeURIComponent(id)}/archive`,
      { method: 'POST', body: JSON.stringify({ reason }) }),
  reinstateRole: (id: string) =>
    request<{ position: Role }>(`/api/v1/roles/${encodeURIComponent(id)}/reinstate`, { method: 'POST' }),
  vacate: (id: string, personId: string, reason: string) =>
    request<{ position: Role; display: string; message?: string; revokedPermissions?: string[] }>(
      `/api/v1/roles/${encodeURIComponent(id)}/vacate`,
      { method: 'POST', body: JSON.stringify({ personId, reason }) }),

  searchPeople: (q: string) =>
    request<{ items: Person[]; count: number; note?: string }>(`/api/v1/admin/people${qs({ q })}`),

  /**
   * Search Entra for somebody the register does not hold.
   *
   * Distinct from searchPeople, which reads the register. A 503 from this one means the deployment
   * has no directory credential — which the caller must show as "we cannot see the directory"
   * rather than as "nobody found".
   */
  lookupDirectory: (q: string, limit = 25) =>
    request<{ items: DirectoryCandidate[]; count: number }>(
      `/api/v1/directory/lookup${qs({ q, limit })}`,
    ),

  addPersonFromDirectory: (id: string) =>
    request<{ person: Person; outcome: string }>(`/api/v1/directory/lookup`, {
      method: 'POST',
      body: JSON.stringify({ id }),
    }),

  assignments: (all = false) =>
    request<{ items: Assignment[]; count: number; bootstrapAdmins?: string[] }>(
      `/api/v1/admin/assignments${qs({ all })}`,
    ),

  grantRole: (body: Record<string, unknown>) =>
    request<{ assignment: Assignment; needsConfirmation: boolean; message: string }>(
      '/api/v1/admin/assignments',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  confirmRole: (id: string) =>
    request<{ assignment: Assignment }>(
      `/api/v1/admin/assignments/${encodeURIComponent(id)}/confirm`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  revokeRole: (id: string, reason: string) =>
    request<{ assignment: Assignment }>(
      `/api/v1/admin/assignments/${encodeURIComponent(id)}/revoke`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  controls: () => request<{ items: Control[]; count: number }>('/api/v1/controls'),
  soa: () =>
    request<{ report: SoAReport; documentsByControl?: Record<string, { linked?: number; candidates?: number }> }>(
      '/api/v1/soa',
    ),

  credentials: (p: { asset: string; location?: string }) =>
    request<{
      items: { credential: Credential; overdue: boolean; lendable: boolean }[]
      count: number
      stats: AccessStats
      canManage: boolean
    }>(`/api/v1/access/credentials${qs(p)}`),

  registerCredential: (body: {
    assetId: string
    locationId: string
    label: string
    kind: string
    description?: string
    zones?: string[]
  }) => request<Credential>('/api/v1/access/credentials', { method: 'POST', body: JSON.stringify(body) }),

  loans: (p: Record<string, string | number | boolean | undefined> = {}) =>
    request<{ items: { loan: Loan; overdue: boolean; daysOut: number }[]; count: number }>(
      `/api/v1/access/loans${qs(p)}`,
    ),

  issue: (body: {
    assetId: string
    credentialId: string
    borrowerName: string
    borrowerPersonId?: string
    borrowerEmail?: string
    borrowerCompany?: string
    borrowerHomeLocationId?: string
    employee: boolean
    purpose?: string
    expectedReturnAt?: string
  }) =>
    request<{ loan: Loan; credential: Credential }>('/api/v1/access/loans', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  returnLoan: (id: string, body: { note?: string; lost?: boolean }) =>
    request<{ loan: Loan; credential: Credential }>(
      `/api/v1/access/loans/${encodeURIComponent(id)}/return`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // AI intake cases. Filing and reading your own need only a sign-in; the queue and every decision
  // are gated by the API, which says which permission is missing.
  /** Who a request can name: any signed-in person; two characters at least, ten results. */
  intakePeople: (q: string) => request<{ items: PersonChoice[] | null }>(`/api/v1/ai-intake/people${qs({ q })}`),
  intakeCatalogue: () => request<IntakeCatalogue>('/api/v1/ai-intake/catalogue'),
  intakePreviewRequest: (body: { toolId: string; scope: IntakeScope; why?: string }) =>
    request<IntakeKind>('/api/v1/ai-intake/preview-request', { method: 'POST', body: JSON.stringify(body) }),
  intakeList: (scope: 'mine' | 'assigned' | 'queue' | 'all') =>
    request<{ cases: IntakeRow[] | null; scope: string }>(`/api/v1/ai-intake${qs({ scope })}`),
  intakeSubmit: (body: IntakeSubmit) =>
    request<{ case: IntakeCase }>('/api/v1/ai-intake', { method: 'POST', body: JSON.stringify(body) }),
  intakeCase: (id: string) => request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}`),
  intakeTriage: (id: string, body: IntakeTriageBody) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/triage`, { method: 'POST', body: JSON.stringify(body) }),
  intakeReassign: (id: string, track: IntakeTrack, reviewerId: string, version: number) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/tracks/${track}/reassign`, {
      method: 'POST', body: JSON.stringify({ reviewerId, version }),
    }),
  intakeReleaseClaim: (id: string, reason: string, version: number) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/release-claim`, { method: 'POST', body: JSON.stringify({ reason, version }) }),
  intakeRouteInDepth: (id: string, body: { reason: string; touchesData?: string; reviewers: Partial<Record<IntakeTrack, string>>; version: number }) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/route-in-depth`, { method: 'POST', body: JSON.stringify(body) }),
  intakeFinding: (id: string, track: IntakeTrack, body: { outcome: string; note: string; version: number }) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/tracks/${track}/finding`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  intakeFastTrack: (id: string, body: IntakeDecisionBody) =>
    request<{ case: IntakeCase; warnings?: string[] | null }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/fast-track-decision`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  intakeCommittee: (id: string, body: IntakeDecisionBody) =>
    request<{ case: IntakeCase; warnings?: string[] | null }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/committee-decision`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  intakeConditionDone: (id: string, cid: string, note: string, version: number) =>
    request<{ case: IntakeCase }>(`/api/v1/ai-intake/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}/complete`, {
      method: 'POST', body: JSON.stringify({ note, version }),
    }),

  // AI classification records. The catalogue is open to anyone signed in; the rest is for
  // governance roles, auditors and the asset's owners, and the API says which.
  classificationCatalogue: () => request<ClassCatalogue>('/api/v1/ai-classifications/catalogue'),
  classifications: () => request<{ items: ClassRow[] | null; count: number }>('/api/v1/ai-classifications'),
  classification: (id: string, version?: number) =>
    request<ClassDetail>(`/api/v1/ai-classifications/${encodeURIComponent(id)}${qs({ version })}`),
  createClassification: (body: Record<string, unknown>) =>
    request<{ record: ClassRecord; warnings: string[] }>('/api/v1/ai-classifications', {
      method: 'POST', body: JSON.stringify(body),
    }),
  patchClassification: (id: string, body: Record<string, unknown>) =>
    request<ClassDetail>(`/api/v1/ai-classifications/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  newClassificationVersion: (id: string) =>
    request<ClassDetail>(`/api/v1/ai-classifications/${encodeURIComponent(id)}/versions`, { method: 'POST' }),
  transitionClassification: (id: string, body: { to: ClassStatus; version: number; reason?: string }) =>
    request<ClassDetail>(`/api/v1/ai-classifications/${encodeURIComponent(id)}/transition`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  signClassification: (id: string, body: {
    cell: ClassCell; version: number; note?: string
    meeting?: { seriesId: string; minuteId: string; decisionId: string }
  }) =>
    request<ClassDetail>(`/api/v1/ai-classifications/${encodeURIComponent(id)}/sign`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  createCondition: (id: string, body: {
    claim: string; consequence: string; cadence: Cadence; owner: { type: 'person' | 'position'; id: string; display?: string }
  }) =>
    request<{ condition: BoundaryCondition; warning?: string }>(`/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  condition: (id: string, cid: string) =>
    request<ConditionDetail>(
      `/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}`,
    ),
  retireCondition: (id: string, cid: string, body: { version: number; reason: string }) =>
    request<{ condition: ConditionDetail }>(
      `/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}`,
      { method: 'PATCH', body: JSON.stringify({ ...body, retire: true }) },
    ),
  confirmCondition: (id: string, cid: string, note: string) =>
    request<ConditionDetail>(
      `/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}/confirm`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),
  reportBreach: (id: string, cid: string, note: string) =>
    request<ConditionDetail>(
      `/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}/breach`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),
  dismissBreach: (id: string, cid: string, reason: string) =>
    request<ConditionDetail>(
      `/api/v1/ai-classifications/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}/dismiss-breach`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
}
